import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Heart, Lock, Coins, Euro } from 'lucide-react';
import ProfileNavTopBar from '../ProfileNavTopBar';
import { useT } from '../../i18n/I18nModule';
import { loadingPageText } from '../../i18n/loadingPage';
import { creditShopProductDisplayName } from '../../i18n/shopProducts';
import { HOROSCOPE_TEEMAPAKETTI_PRODUCT_ID } from './avatarDesignerZodiac';
import {
  API,
  EVENT_PROMO_CREDIT_PRODUCT_IDS,
  GAME_SUGGESTION_CREDIT_PRODUCT_IDS,
  PAIVAN_KYSYMYS_CREDIT_PRODUCT_IDS,
  POST_PROMO_CREDIT_PRODUCT_IDS,
} from './profileHelpers';
import { PULINA_CREDIT_PURCHASES_CHANGED } from '../../useStatisticsUnlock';

function shopColumnLabel(tier) {
  return `LVL ${tier}`;
}

const MAX_MEMBER_LEVEL = 4;

function clampMemberLevel(level) {
  const n = Number(level);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(MAX_MEMBER_LEVEL, Math.floor(n));
}

function blockHint(t, blockReason, requiredLevel) {
  if (blockReason === 'NOT_READY') return t('shop.status.notReady');
  if (blockReason === 'LEVEL') {
    return t('shop.status.levelRequired').replace('{level}', String(requiredLevel));
  }
  if (blockReason === 'PREREQ') return t('shop.status.prerequisite');
  if (blockReason === 'CREDITS') return t('shop.status.insufficientCredits');
  if (blockReason === 'ALREADY_OWNED') return t('shop.status.bought');
  return '';
}

/** Card icon (heart or lock): gray = not implemented / level lock, green = one-time owned or (horoskoopit) ready to buy, red = can buy or insufficient PC. */
function creditShopHeartClassName(p, notReady) {
  const base = 'CreditShop-card-heart';
  if (notReady) return `${base} ${base}--not-ready`;
  if (p.blockReason === 'CREDITS') return `${base} ${base}--no-credits`;
  const oneTimeOwned =
    p.blockReason === 'ALREADY_OWNED' ||
    (Number(p.ownedQuantity) > 0 && !p.recurring && !p.inventoryItem);
  if (oneTimeOwned) return `${base} ${base}--owned`;
  if (p.canPurchase) {
    if (p.id === HOROSCOPE_TEEMAPAKETTI_PRODUCT_ID) return `${base} ${base}--owned`;
    return `${base} ${base}--can-buy`;
  }
  return `${base} ${base}--locked`;
}

/**
 * Memoized so parent state (e.g. not-ready toast) does not re-render every row / Lucide icon on Android.
 */
const CreditShopProductCard = memo(function CreditShopProductCard({
  product: p,
  purchaseBusy,
  onBuy,
  onNotReadyTap,
  t,
}) {
  const displayName = creditShopProductDisplayName(p, t) || p.productName;
  const notReady = (p.requiredLevel ?? 1) <= 0 || p.blockReason === 'NOT_READY';
  const cardDarkened = p.blockReason === 'LEVEL' || p.blockReason === 'CREDITS';
  const notReadyLabel = t('shop.status.notReady');
  const hint = blockHint(t, p.blockReason, p.requiredLevel);

  const iconClass = creditShopHeartClassName(p, notReady);
  const cardBody = (
    <>
      {p.blockReason === 'LEVEL' ? (
        <Lock className={iconClass} size={28} strokeWidth={1.75} aria-hidden />
      ) : (
        <Heart
          className={iconClass}
          size={28}
          strokeWidth={1.75}
          fill="currentColor"
          aria-hidden
        />
      )}
      <span className="CreditShop-card-name">{displayName}</span>
      {p.inventoryItem && p.ownedQuantity > 0 && (
        <span className="CreditShop-card-qty">×{p.ownedQuantity}</span>
      )}
      <span className="CreditShop-card-price">
        {p.productPrice}
        <Coins size={14} strokeWidth={2} className="CreditShop-card-coin" aria-hidden />
      </span>
      {notReady ? (
        <span className="CreditShop-card-hint">{notReadyLabel}</span>
      ) : (
        !p.canPurchase && <span className="CreditShop-card-hint">{hint}</span>
      )}
    </>
  );

  if (notReady) {
    return (
      <div
        className="CreditShop-card CreditShop-card--not-ready CreditShop-card--darkened"
        title={notReadyLabel}
        role="group"
        aria-label={`${displayName}. ${notReadyLabel}.`}
        onClick={onNotReadyTap}
      >
        {cardBody}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`CreditShop-card${cardDarkened ? ' CreditShop-card--darkened' : ''}`}
      disabled={!p.canPurchase || purchaseBusy}
      onClick={() => onBuy(p)}
      title={hint}
    >
      {cardBody}
    </button>
  );
});

export default function KauppaPage({ user }) {
  const t = useT();
  const userId = user?.id;
  const userIdStr = userId != null ? String(userId).replace(/n$/, '') : '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(0);
  const [memberLevel, setMemberLevel] = useState(1);
  const [products, setProducts] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [notReadyToast, setNotReadyToast] = useState(false);
  const notReadyToastTimerRef = useRef(null);
  /** 'credits' = PC shop; 'euro' = money shop (placeholder). */
  const [shopMode, setShopMode] = useState('credits');
  const displayMemberLevel = clampMemberLevel(memberLevel);

  const load = useCallback(() => {
    if (!userIdStr) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/api/users/${encodeURIComponent(userIdStr)}/credit-shop`)
      .then(async (res) => {
        if (res.status === 403) throw new Error('Jäsenille tarkoitettu sivu.');
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const detail =
            (typeof data.message === 'string' && data.message) ||
            (typeof data.error === 'string' && data.error) ||
            '';
          throw new Error(detail ? `${detail} (${res.status})` : `Lataus epäonnistui (${res.status}).`);
        }
        return res.json();
      })
      .then((data) => {
        setBalance(typeof data.balance === 'number' ? data.balance : 0);
        setMemberLevel(
          clampMemberLevel(typeof data.memberLevel === 'number' ? data.memberLevel : 1),
        );
        setProducts(Array.isArray(data.products) ? data.products : []);
      })
      .catch((e) => setError(e.message || 'Virhe'))
      .finally(() => setLoading(false));
  }, [userIdStr]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (notReadyToastTimerRef.current != null) {
        clearTimeout(notReadyToastTimerRef.current);
      }
    },
    [],
  );

  const showNotReadyFlash = useCallback(() => {
    setNotReadyToast(true);
    if (notReadyToastTimerRef.current != null) clearTimeout(notReadyToastTimerRef.current);
    notReadyToastTimerRef.current = setTimeout(() => {
      setNotReadyToast(false);
      notReadyToastTimerRef.current = null;
    }, 2200);
  }, []);

  const byTier = useMemo(() => {
    const m = { 1: [], 2: [], 3: [], 4: [] };
    for (const p of products) {
      const t = Number(p.tier);
      if (m[t]) m[t].push(p);
    }
    return m;
  }, [products]);

  /** Gauge is full at 4000 PC; ticks every 500 PC. */
  const CREDIT_GAUGE_MAX_PC = 4000;
  const CREDIT_GAUGE_TICK_STEP_PC = 500;
  const gaugeTicks = useMemo(() => {
    const ticks = [];
    for (let v = 0; v <= CREDIT_GAUGE_MAX_PC; v += CREDIT_GAUGE_TICK_STEP_PC) {
      ticks.push(v);
    }
    return ticks;
  }, []);
  const barPct = Math.min(100, Math.max(0, (balance / CREDIT_GAUGE_MAX_PC) * 100));

  const handleBuy = useCallback(
    (p) => {
      if (!p.canPurchase || buyingId) return;
      const ok = window.confirm(
        `Ostetaanko tuote "${creditShopProductDisplayName(p, t)}" hintaan ${p.productPrice} PC?`
      );
      if (!ok) return;
      setBuyingId(p.id);
      fetch(`${API}/api/users/${encodeURIComponent(userIdStr)}/credit-shop/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: p.id }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            const msg = data.message || data.error || 'Osto epäonnistui.';
            window.alert(msg);
            return;
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(PULINA_CREDIT_PURCHASES_CHANGED));
          }
          const purchased = String(data.purchasedProductId ?? p.id).trim();
          let successMsg = 'Osto onnistui!';
          if (data.postPromoFormUrl) {
            successMsg =
              'Osto onnistui! Valitse mainostettava postaus linkistä, jonka lähetimme sinulle yksityisviestinä.';
          } else if (data.eventPromoFormUrl) {
            successMsg =
              'Osto onnistui! Valitse mainostettavatapahtumasi linkistä, joka lähetettiin Sinulle yksityisviestinä.';
          } else if (data.gameSuggestionId) {
            successMsg =
              'Osto onnistui! Täytä ehdotus linkistä, joka lähetettiin Sinulle yksityisviestinä.';
          } else if (GAME_SUGGESTION_CREDIT_PRODUCT_IDS.has(purchased)) {
            successMsg =
              'Osto onnistui! Täytä ehdotus linkistä, joka lähetettiin Sinulle yksityisviestinä.';
          } else if (PAIVAN_KYSYMYS_CREDIT_PRODUCT_IDS.has(purchased)) {
            successMsg =
              'Osto onnistui! Lisää kysymyksesi linkistä, joka lähetettiin sinulle yksityisviestinä.';
          } else if (EVENT_PROMO_CREDIT_PRODUCT_IDS.has(purchased)) {
            successMsg =
              'Osto onnistui! Valitse tapahtumasi linkistä, joka lähetettiin sinulle yksityisviestinä.';
          } else if (POST_PROMO_CREDIT_PRODUCT_IDS.has(purchased)) {
            successMsg =
              'Osto onnistui! Valitse mainostettava postaus linkistä, jonka lähetimme sinulle yksityisviestinä.';
          }
          window.alert(successMsg);
          window.location.reload();
        })
        .catch(() => window.alert('Yhteysvirhe.'))
        .finally(() => setBuyingId(null));
    },
    [userIdStr, buyingId, t],
  );

  return (
    <div className="App-block App-block--credit-shop">
      <ProfileNavTopBar user={user} currentPage="kauppa" />
      <div className="CreditShop">
        <div className="CreditShop-header-row">
          <div className="Profile-header CreditShop-profile-header">
            <h2 className="Profile-heading CreditShop-heading">
              {shopMode === 'euro' ? 'Kauppa' : 'Pulina Credits (PC)'}
            </h2>
          </div>
          <div className="CreditShop-mode-toggle" role="group" aria-label="Valitse kaupan tila">
            <button
              type="button"
              className={`CreditShop-mode-btn${shopMode === 'credits' ? ' CreditShop-mode-btn--active' : ''}`}
              onClick={() => setShopMode('credits')}
              aria-pressed={shopMode === 'credits'}
              title="Pulina-krediitit (PC)"
            >
              <Coins size={20} strokeWidth={2} aria-hidden />
              <span className="CreditShop-mode-btn-label">PC</span>
            </button>
            <button
              type="button"
              className={`CreditShop-mode-btn${shopMode === 'euro' ? ' CreditShop-mode-btn--active' : ''}`}
              onClick={() => setShopMode('euro')}
              aria-pressed={shopMode === 'euro'}
              title="Eurokauppa"
            >
              <Euro size={20} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>
        {shopMode === 'euro' ? (
          <div className="CreditShop-euro-placeholder" role="status">
            <p>{t('shop.euroPlaceholder')}</p>
          </div>
        ) : loading ? (
          <p>{loadingPageText(t, 'shop')}</p>
        ) : error ? (
          <p className="CreditShop-error">{error}</p>
        ) : (
          <>
            <div
              className="CreditShop-balance-bar-wrap"
              aria-label={`Saldo ${balance} PC (asteikko 0–${CREDIT_GAUGE_MAX_PC} PC), jäsentaso ${displayMemberLevel}`}
            >
              <div className="CreditShop-balance-bar">
                <div
                  className="CreditShop-balance-fill"
                  style={{ width: `${barPct}%` }}
                >
                  <span className="CreditShop-balance-label">{balance} PC</span>
                </div>
                <div className="CreditShop-balance-ticks" aria-hidden="true">
                  {gaugeTicks.map((tick) => (
                    <span
                      key={tick}
                      className="CreditShop-balance-tick"
                      style={{ left: `${(tick / CREDIT_GAUGE_MAX_PC) * 100}%` }}
                    />
                  ))}
                </div>
                <span className="CreditShop-balance-lvl">LVL {displayMemberLevel}</span>
              </div>
              <div className="CreditShop-balance-scale" aria-hidden="true">
                {gaugeTicks.map((tick) => (
                  <span key={tick} style={{ left: `${(tick / CREDIT_GAUGE_MAX_PC) * 100}%` }}>
                    {tick} PC
                  </span>
                ))}
              </div>
            </div>
            <div className="CreditShop-grid">
              {[1, 2, 3, 4].map((tier) => {
                const tierTitle = shopColumnLabel(tier);
                const list = byTier[tier] || [];
                return (
                  <section key={tier} className="CreditShop-column" aria-label={tierTitle}>
                    <h3 className="CreditShop-tier-title">
                      {tierTitle}
                    </h3>
                    <div className="CreditShop-cards">
                      {list.map((p) => (
                        <CreditShopProductCard
                          key={p.id}
                          product={p}
                          purchaseBusy={!!buyingId}
                          onBuy={handleBuy}
                          onNotReadyTap={showNotReadyFlash}
                          t={t}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        )}
        {shopMode === 'credits' && notReadyToast ? (
          <div className="CreditShop-not-ready-flash" role="status" aria-live="polite">
            {t('shop.status.notReady')}
          </div>
        ) : null}
      </div>
    </div>
  );
}
